import React, { useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { CHARACTERISTICS } from '../incidentConstants'
import PersonInvolvedDialog, { getId } from '../PersonInvolvedDialog'
import PersonInvolvedCard from '../PersonInvolvedCard'

const SectionHeader = ({ title, caption, action }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    flexWrap="wrap"
    useFlexGap
  >
    <Box minWidth={0}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
    {action && <Box flexShrink={0}>{action}</Box>}
  </Stack>
)

const InfoBox = ({ incident, handleChange }) => {
  const people = incident?.peopleInvolved ?? []
  const hiddenFrom = incident?.hiddenFrom ?? []
  const [dialog, setDialog] = useState(null) // { index } | null
  const editing =
    dialog && dialog.index !== null && dialog.index !== undefined
      ? people[dialog.index]
      : null

  const isHiddenFrom = (person) => {
    const employeeId = getId(person?.employee)
    return !!employeeId && hiddenFrom.some((x) => getId(x) === employeeId)
  }

  // Keeps the Review → Hidden From selector in sync: it holds populated
  // employee objects, so push the selected employee object (same shape as
  // the StaffSelector options) rather than a bare id.
  const setHidden = (employee, hidden) => {
    const employeeId = getId(employee)
    if (!employeeId) return
    const currentlyHidden = hiddenFrom.some((x) => getId(x) === employeeId)
    if (currentlyHidden === hidden) return
    const without = hiddenFrom.filter((x) => getId(x) !== employeeId)
    handleChange('hiddenFrom', hidden ? [...without, employee] : without)
  }

  const savePerson = ({ person, hideFromEmployee }) => {
    handleChange(
      'peopleInvolved',
      editing
        ? people.map((x, i) => (i === dialog.index ? person : x))
        : [...people, person],
    )
    setHidden(person.employee, hideFromEmployee)
  }

  const removePerson = (index) =>
    handleChange(
      'peopleInvolved',
      people.filter((person, i) => i !== index),
    )

  return (
    <Stack gap={3}>
      <Stack gap={1.5}>
        <SectionHeader
          title="People Involved"
          caption="Add, correct or remove people — all changes are audit-logged on save."
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ index: null })}
            >
              Add Person
            </Button>
          }
        />
        {people.length === 0 && (
          <Alert severity="info" icon={<PersonOutlineIcon />} sx={{ borderRadius: 2 }}>
            No people are recorded as involved in this incident.
          </Alert>
        )}
        <Grid container spacing={1.5}>
          {people.map((person, index) => (
            <Grid item xs={12} md={6} key={person._id ?? index}>
              <PersonInvolvedCard
                person={person}
                index={index}
                hidden={isHiddenFrom(person)}
                onEdit={() => setDialog({ index })}
                onRemove={() => removePerson(index)}
              />
            </Grid>
          ))}
        </Grid>
        <PersonInvolvedDialog
          open={!!dialog}
          onClose={() => setDialog(null)}
          onSave={savePerson}
          person={editing}
          isHidden={editing ? isHiddenFrom(editing) : false}
        />
      </Stack>

      <Divider />

      <Stack gap={1.5}>
        <SectionHeader title="Equipment & Assets" />
        <TextField
          label="Related Asset"
          value={incident?.relatedAsset ?? ''}
          onChange={(e) => handleChange('relatedAsset', e.target.value)}
          size="small"
          sx={{ maxWidth: 400 }}
          autoComplete="off"
        />
        {incident?.vehiclesInvolved?.length > 0 && (
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Vehicles involved:
            </Typography>
            {incident.vehiclesInvolved.map((vehicle, idx) => (
              <Chip
                key={vehicle?._id ?? idx}
                size="small"
                variant="outlined"
                label={
                  vehicle?.callSign
                    ? `${vehicle.callSign}${vehicle.regPlate ? ` · ${vehicle.regPlate}` : ''}`
                    : 'Unknown vehicle'
                }
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {incident.relatedMedicines?.length > 0 && (
        <>
          <Divider />
          <Stack gap={1.5}>
            <SectionHeader
              title="Medicines Involved"
              caption="Stock removed from a bag as part of this incident."
            />
            <Grid container spacing={1.5}>
              {incident.relatedMedicines.map((x, idx) => (
                <Grid item xs={12} lg={6} xl={4} key={idx}>
                  <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                    <CardContent sx={{ '&:last-child': { pb: 1 }, p: 1 }}>
                      <Box
                        display="flex"
                        flexDirection={{ xs: 'column', md: 'row' }}
                        gap={2}
                      >
                        <Box flex={1}>
                          <Stack spacing={0.5}>
                            <Typography variant="h6" color="primary">
                              {x.medicine?.title ?? 'Unknown Medicine'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {x.medicine?.presentation} •{' '}
                              {x.medicine?.constitution}
                            </Typography>
                            {x.medicine?.level?.length > 0 && (
                              <Typography variant="body2">
                                <strong>Medication Level:</strong>{' '}
                                {x.medicine.level.join(', ')}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              <strong>Batch Number:</strong> {x.batchNumber}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Expiry Date:</strong>{' '}
                              {x.expiry
                                ? moment(x.expiry).format('Do MMM YYYY')
                                : '—'}
                            </Typography>
                          </Stack>
                        </Box>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            display: { xs: 'none', md: 'block' },
                            alignSelf: 'stretch',
                          }}
                        />
                        <Box flex={1}>
                          <Stack spacing={0.5}>
                            {x.location && (
                              <>
                                <Typography variant="h7" mt={1} mb={2}>
                                  Medicine Bag/Store
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Name:</strong> {x.location.name}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Type:</strong> {x.location.type}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Number:</strong> {x.location.number}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Level:</strong> {x.location.level}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Current Location:</strong>{' '}
                                  {x.location.currentLocation}
                                </Typography>
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </>
      )}

      {incident.medicinesInvolvedNoAction?.length > 0 && (
        <>
          <Divider />
          <Stack gap={1.5}>
            <SectionHeader
              title="Other Medicines Involved"
              caption="Referenced for context — no stock action was taken."
            />
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              {incident.medicinesInvolvedNoAction.map((med, idx) => (
                <Card
                  key={med?._id ?? idx}
                  variant="outlined"
                  sx={{ borderRadius: 2.5 }}
                >
                  <CardContent sx={{ '&:last-child': { pb: 1 }, p: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {med?.title ?? 'Unknown Medicine'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {med?.presentation}{' '}
                      {med?.constitution && `• ${med.constitution}`}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Stack>
        </>
      )}

      <Divider />

      <Stack gap={1}>
        <SectionHeader
          title="Characteristics"
          caption="Flags used for governance reporting and CQC evidence."
        />
        <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={3}>
          {CHARACTERISTICS.map((characteristic) => (
            <FormControlLabel
              key={characteristic.key}
              label={characteristic.label}
              control={
                <Checkbox
                  checked={!!incident?.[characteristic.key]}
                  onChange={(e) =>
                    handleChange(characteristic.key, e.target.checked)
                  }
                  color="success"
                />
              }
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default InfoBox
